import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Trash2, Plus, Send, ExternalLink, Check, Loader2, ArrowRight, Bot, CheckCircle2, AlertTriangle, RefreshCw, Power } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../../lib/api";
import { PageTitle } from "./Layout";
import SecurityTab from "./SecurityTab";
import HistoryTab from "./HistoryTab";
import SystemHealth from "./SystemHealth";
import GoogleDriveBackup from "../../components/admin/GoogleDriveBackup";
import DriveBackupCard from "../../components/admin/DriveBackupCard";
import MigrationTool from "../../components/admin/MigrationTool";
import FullDbTransfer from "../../components/admin/FullDbTransfer";
import CommTemplates from "../../components/admin/CommTemplates";
import PushSettings from "../../components/admin/PushSettings";
import GoogleCalendarSync from "../../components/admin/GoogleCalendarSync";
import CardCollapse from "../../components/admin/CardCollapse";
import { ShieldCheck, Save, Mail, Send as SendIcon, Bot as BotIcon, MessageSquare, Smartphone, Phone } from "lucide-react";

function RecaptchaCard() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["admin", "recaptcha"], queryFn: () => api.get("/admin/integrations/recaptcha").then((r) => r.data) });
  const [f, setF] = useState({ sitekey: "", secret: "", enabled: false, threshold: 0.5 });
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  useEffect(() => { if (data) setF({ sitekey: data.sitekey || "", secret: "", enabled: !!data.enabled, threshold: data.threshold ?? 0.5 }); }, [data]);
  const save = async () => {
    try {
      await api.put("/admin/integrations/recaptcha", f);
      toast.success("reCAPTCHA ayarları kaydedildi");
      setF({ ...f, secret: "" });
      qc.invalidateQueries({ queryKey: ["admin", "recaptcha"] });
    } catch (e) { toast.error(errMsg(e)); }
  };
  const liveTest = async () => {
    setTesting(true); setTestResult(null);
    try {
      // 1) Load Google script
      const sitekey = (data?.sitekey || f.sitekey || "").trim();
      if (!sitekey) throw new Error("Sitekey yok");
      if (!window.grecaptcha) {
        await new Promise((resolve, reject) => {
          const sc = document.createElement("script");
          sc.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(sitekey)}`;
          sc.async = true; sc.defer = true;
          sc.onload = () => resolve(); sc.onerror = () => reject(new Error("Script yüklenemedi"));
          document.head.appendChild(sc);
        });
      }
      // 2) Execute to generate token
      const token = await new Promise((resolve, reject) => {
        window.grecaptcha.ready(() => {
          window.grecaptcha.execute(sitekey, { action: "contact_form" }).then(resolve).catch((e) => reject(new Error(String(e))));
        });
      });
      // 3) Verify server-side
      const r = await api.post("/admin/integrations/recaptcha/test", { token, action: "contact_form" });
      setTestResult(r.data);
    } catch (e) { setTestResult({ error: errMsg(e) || e?.message || "Hata" }); }
    setTesting(false);
  };
  const secretSet = !!data?.secret_configured;
  const chip = (
    <label className="flex items-center gap-2 text-sm" onClick={(e) => e.stopPropagation()}>
      <Switch checked={f.enabled} onCheckedChange={(v) => setF({ ...f, enabled: v })} data-testid="recaptcha-enabled" />
      <span className="text-xs text-stone-500">Aktif</span>
    </label>
  );
  return (
    <CardCollapse
      testid="integration-recaptcha"
      icon={<ShieldCheck size={16} />}
      title="Google reCAPTCHA v3 (bot koruması)"
      description="İletişim ve hizmet talep formlarını otomatik botlardan korur. Boş bırakılırsa pasif kalır — form çalışmaya devam eder."
      chip={chip}
    >
      <ol className="text-xs text-stone-600 list-decimal pl-5 space-y-0.5 bg-stone-50 p-3 rounded">
        <li><a className="underline" href="https://www.google.com/recaptcha/admin/create" target="_blank" rel="noopener noreferrer">reCAPTCHA Admin Console</a> → <strong>reCAPTCHA v3</strong> seç</li>
        <li>Domain olarak sitenin alan adını <strong>ve preview domainini</strong> ekle (örn. <code className="bg-stone-100 px-1 rounded">{window.location.hostname}</code>)</li>
        <li>Site key ve Secret key'i aşağıya yapıştır</li>
      </ol>
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="Site Key"><Input value={f.sitekey} onChange={(e) => setF({ ...f, sitekey: e.target.value })} placeholder="6Lc..." data-testid="recaptcha-sitekey" /></Field>
        <Field label={secretSet ? "Secret Key (kayıtlı — değiştirmek için yazın)" : "Secret Key"}><Input type="password" autoComplete="new-password" value={f.secret} onChange={(e) => setF({ ...f, secret: e.target.value })} placeholder={secretSet ? "••••••••" : "6Lc..."} data-testid="recaptcha-secret" /></Field>
      </div>
      <Field label={`Skor eşiği: ${f.threshold.toFixed(2)}`} hint="0.0 (çok gevşek) — 1.0 (çok sıkı). Önerilen: 0.5">
        <input type="range" min="0" max="1" step="0.05" value={f.threshold} onChange={(e) => setF({ ...f, threshold: parseFloat(e.target.value) })} className="w-full" data-testid="recaptcha-threshold" />
      </Field>
      <div className="flex items-center gap-2 flex-wrap">
        <Button type="button" onClick={save} data-testid="recaptcha-save"><Save size={14} className="mr-1.5" />Kaydet</Button>
        {secretSet && f.enabled && (
          <Button type="button" variant="outline" onClick={liveTest} disabled={testing} data-testid="recaptcha-live-test">
            {testing ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <ShieldCheck size={14} className="mr-1.5" />}Canlı test
          </Button>
        )}
      </div>
      {testResult && (
        <div className={`text-xs rounded-lg border p-3 ${testResult.passes ? "bg-emerald-50 border-emerald-200 text-emerald-900" : "bg-rose-50 border-rose-200 text-rose-900"}`} data-testid="recaptcha-test-result">
          {testResult.error ? (
            <div><strong>Hata:</strong> {testResult.error}</div>
          ) : testResult.passes ? (
            <div>
              <div className="font-semibold">✓ reCAPTCHA bu domainde çalışıyor</div>
              <div className="mt-1">Skor: <code className="font-mono">{testResult.score}</code> · Eşik: <code className="font-mono">{testResult.threshold}</code> · Hostname: <code className="font-mono">{testResult.hostname}</code></div>
            </div>
          ) : (
            <div>
              <div className="font-semibold">✗ Doğrulama başarısız</div>
              <ul className="mt-1 space-y-0.5 list-disc pl-5">
                {!testResult.success && <li>Google <code>success=false</code> döndürdü{testResult.error_codes?.length ? ` (${testResult.error_codes.join(", ")})` : ""}</li>}
                {testResult.action && testResult.action !== testResult.expected_action && <li>Action uyumsuz: {testResult.action} ≠ {testResult.expected_action}</li>}
                {typeof testResult.score === "number" && testResult.score < testResult.threshold && <li>Skor eşiğin altında: {testResult.score} &lt; {testResult.threshold}</li>}
                {testResult.hostname && <li>Hostname: <code>{testResult.hostname}</code></li>}
                {testResult.error_codes?.includes("invalid-input-response") && <li className="font-semibold">Büyük ihtimalle: Bu domain Google reCAPTCHA Admin'de yetkilendirilmemiş. <a className="underline" href="https://www.google.com/recaptcha/admin" target="_blank" rel="noopener noreferrer">Admin paneline git</a> → Settings → Domains → <code>{window.location.hostname}</code> ekle.</li>}
              </ul>
            </div>
          )}
        </div>
      )}
    </CardCollapse>
  );
}
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Button } from "../../components/ui/button";
import { Switch } from "../../components/ui/switch";
import { Field, I18nInput, ImageUpload } from "../../components/admin/Fields";
import { useSection, Card, Row, VisGroup, Vis, Fold } from "../../components/admin/Section";
import { useAdmin } from "./AdminApp";
import RichEditor from "../../components/admin/RichEditor";
import { TextStyleEditor } from "../../components/admin/TextStyleEditor";

function General() {
  const { f, set, save, busy, setSave } = useSection("general");
  return (
    <Card title="Genel" onSave={save} busy={busy} testid="settings-general">
      <I18nInput label="Site adı" value={f.site_name} onChange={set("site_name")} testid="general-site-name" maxLength={80} />
      <TextStyleEditor title="Site adı görünümü (yazı tipi ve renk)" value={f.site_name_style} onChange={set("site_name_style")} sample={f.site_name?.tr} parts={["Ad", "Soyad"]} testid="site-name-style" />
      <div className="grid md:grid-cols-2 gap-5">
        <ImageUpload label="Logo (boşsa imza stilinde ad)" value={f.logo} onChange={setSave("logo")} testid="general-logo" alt="logo" />
        <ImageUpload label="Favicon" value={f.favicon} onChange={setSave("favicon")} testid="general-favicon" alt="favicon" />
      </div>
      <p className="text-xs text-stone-500">E-posta, telefon, adres ve KVKK metni artık Ayarlar &gt; İletişim &gt; Anasayfa iletişim alanı sekmesinde.</p>
      <Fold title="Bakım modu (yakında sayfası)" testid="general-fold-maintenance">
        <label className="flex items-center gap-3 text-sm text-stone-700 mb-4">
          <Switch checked={!!f.maintenance} onCheckedChange={set("maintenance")} data-testid="general-maintenance-toggle" />
          Bakım modunu aç (ziyaretçiler "Pek Yakında" ekranını görür, yönetim paneli açık kalır)
        </label>
        <I18nInput label="Başlık" value={f.maintenance_title} onChange={set("maintenance_title")} testid="general-maintenance-title" maxLength={80} />
        <I18nInput label="Alt metin" value={f.maintenance_text} onChange={set("maintenance_text")} multiline rows={3} testid="general-maintenance-text" maxLength={300} />
        <p className="text-xs text-stone-500 mt-2">Boş bırakırsanız varsayılan "Pek Yakında" metni gösterilir. Giriş yaptığınızda siteyi normal görüntülemeye devam edersiniz.</p>
      </Fold>
    </Card>
  );
}

const PROVIDERS = [["openai", "OpenAI", "gpt-4o-mini"], ["anthropic", "Anthropic (Claude)", "claude-haiku-4-5"], ["gemini", "Google (Gemini)", "gemini-2.5-flash"], ["openrouter", "OpenRouter (diğer motorlar)", "openai/gpt-4o-mini"]];

function AI() {
  const { f, set, save, busy } = useSection("ai");
  const [keys, setKeys] = useState({});
  const { data: usage } = useQuery({ queryKey: ["admin", "ai-usage"], queryFn: () => api.get("/admin/ai/usage").then((r) => r.data) });
  const submit = async () => { await save({ ...f, keys }); setKeys({}); };
  return (
    <Card title="Yapay Zeka" sub="Anahtarlar şifrelenerek saklanır; panelde yalnızca son 4 karakter görünür ve tarayıcıya hiç gönderilmez." onSave={submit} busy={busy} testid="settings-ai">
      {PROVIDERS.map(([k, label, def]) => (
        <div key={k} className="grid md:grid-cols-[1fr_1fr_auto] gap-3 items-end border rounded-lg p-4" data-testid={`ai-provider-${k}`}>
          <Field label={`${label} API anahtarı`} hint={f.keys?.[k] ? `Kayıtlı: ${f.keys[k]}` : "Tanımlı değil"}>
            <Input type="password" autoComplete="new-password" placeholder={f.keys?.[k] ? "Değiştirmek için yeni anahtar girin" : "sk-…"} value={keys[k] || ""} onChange={(e) => setKeys({ ...keys, [k]: e.target.value })} data-testid={`ai-key-${k}`} />
          </Field>
          <Field label="Model"><Input placeholder={def} value={f.models?.[k] || ""} onChange={(e) => set("models")({ ...f.models, [k]: e.target.value })} data-testid={`ai-model-${k}`} /></Field>
          {f.keys?.[k] ? <Button type="button" variant="ghost" size="sm" onClick={() => setKeys({ ...keys, [k]: "__delete__" })} data-testid={`ai-delete-${k}`}>{keys[k] === "__delete__" ? "Silinecek" : "Sil"}</Button> : <span />}
        </div>
      ))}
      <div className="grid md:grid-cols-3 gap-5">
        <Field label="Çeviri için kullanılacak motor">
          <select className="w-full border rounded-md h-9 px-3 text-sm bg-white" value={f.provider || ""} onChange={(e) => set("provider")(e.target.value)} data-testid="ai-provider-select">
            <option value="">— Kapalı —</option>
            {PROVIDERS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </Field>
        <Field label="Günlük istek limiti"><Input type="number" min={0} value={f.daily_limit ?? 50} onChange={(e) => set("daily_limit")(Number(e.target.value))} data-testid="ai-daily-limit" /></Field>
        <Field label="Aylık istek limiti"><Input type="number" min={0} value={f.monthly_limit ?? 500} onChange={(e) => set("monthly_limit")(Number(e.target.value))} data-testid="ai-monthly-limit" /></Field>
      </div>
      <div data-testid="ai-usage">
        <p className="text-sm font-semibold">Kullanım: bugün {usage?.today ?? 0} · bu ay {usage?.month ?? 0}</p>
        <div className="mt-2 max-h-48 overflow-y-auto text-xs divide-y border rounded-lg">
          {(usage?.history || []).map((h) => <div key={h.id} className="px-3 py-2 flex justify-between"><span>{h.provider} · {h.model} · {h.chars} karakter</span><span className={h.ok ? "text-green-600" : "text-red-600"}>{h.ok ? "başarılı" : h.error} · {new Date(h.at).toLocaleString("tr-TR")}</span></div>)}
          {!usage?.history?.length && <p className="px-3 py-3 text-stone-400">Henüz kullanım yok.</p>}
        </div>
      </div>
    </Card>
  );
}

function ContactArea() {
  const { f, set, save, busy, setSave } = useSection("general");
  const { settings } = useAdmin();
  const profilePhoto = settings.profile?.blog_photo || settings.profile?.photo || "";
  return (
    <Card title="İletişim" sub="Anasayfa iletişim bölümü ve İletişim sayfasında görünen bilgiler. Bölümleri açıp kapatarak düzenleyin." onSave={save} busy={busy} testid="contact-area-card">
      <Fold title="Başlık ve açıklama" testid="contact-fold-head">
        <Row f={f} set={set} k="contact_eyebrow"><I18nInput label="Üst başlık (isteğe bağlı)" value={f.contact_eyebrow} onChange={set("contact_eyebrow")} testid="contact-eyebrow-input" maxLength={80} /></Row>
        <Row f={f} set={set} k="contact_title"><I18nInput label="Başlık" value={f.contact_title} onChange={set("contact_title")} testid="contact-title-input" maxLength={120} /></Row>
        <Row f={f} set={set} k="contact_text"><RichEditor label="Açıklama yazısı" value={f.contact_text} onChange={set("contact_text")} testid="contact-text-editor" minH="min-h-[120px]" /></Row>
        <Fold title="Yazı tipi ve renk (gelişmiş)" testid="contact-fold-styles">
          <TextStyleEditor title="Üst başlık görünümü" value={f.contact_eyebrow_style} onChange={set("contact_eyebrow_style")} sample={f.contact_eyebrow?.tr || "Bana ulaşın"} testid="contact-eyebrow-style" />
          <TextStyleEditor title="Başlık görünümü" value={f.contact_title_style} onChange={set("contact_title_style")} sample={f.contact_title?.tr || "İletişim ve İş Birliği"} testid="contact-title-style" />
        </Fold>
      </Fold>
      <Fold title="İletişim bilgileri" testid="contact-fold-info">
        <Row f={f} set={set} k="email"><Field label="E-posta"><Input value={f.email || ""} onChange={set("email")} data-testid="general-email" /></Field></Row>
        <Row f={f} set={set} k="phone"><Field label="Telefon"><Input value={f.phone || ""} onChange={set("phone")} data-testid="general-phone" /></Field></Row>
        <VisGroup title="Diğer öğeler" f={f} set={set} items={[["socials", "Sosyal hesap ikonları"], ["form", "İletişim formu"]]} />
      </Fold>
      <Fold title="İletişim sayfası fotoğrafı" testid="contact-fold-photo">
        <ImageUpload label="Fotoğraf (boş bırakılırsa profil fotoğrafınız kullanılır)" value={f.contact_photo || profilePhoto} onChange={setSave("contact_photo")} testid="contact-page-photo" alt="iletişim fotoğrafı" />
        <div className="flex flex-wrap gap-6">
          <Vis f={f} set={set} k="contact_photo" label="Fotoğrafı göster" />
          <label className="flex items-center gap-2 text-xs text-stone-600 whitespace-nowrap"><Switch checked={f.contact_photo_hover !== false} onCheckedChange={(v) => set("contact_photo_hover")(v)} data-testid="vis-contact-photo-hover" />Hareket efekti (üzerine gelince)</label>
          <Vis f={f} set={set} k="contact_page_text" label="Açıklama yazısını göster" />
        </div>
      </Fold>
      <Fold title="Hizmet talep modalı — fotoğraf ve etiket" testid="contact-fold-inquiry">
        <p className="text-xs text-stone-500 mb-2">Çalışmalarım sayfasında "Birlikte Değerlendirelim" butonuna basınca açılan modalın solunda gösterilen fotoğraf ve altındaki ad/alt satır bu alanlardan yönetilir. Boş bırakırsan İletişim sayfası fotoğrafı (ya da profil fotoğrafı) ve profil adı kullanılır.</p>
        <ImageUpload label="Modal fotoğrafı (opsiyonel)" value={f.inquiry_photo || ""} onChange={setSave("inquiry_photo")} testid="inquiry-photo" alt="iletişim modal fotoğrafı" />
        <div className="grid sm:grid-cols-2 gap-3 mt-3">
          <Field label="Fotoğraf üzerindeki ad (default: profil adı)"><Input value={f.inquiry_name || ""} onChange={set("inquiry_name")} placeholder="Olgu Tansuğ" data-testid="inquiry-name-input" /></Field>
          <Field label="Fotoğraf altı alt yazı (default: hizmet adı)"><Input value={f.inquiry_subtitle || ""} onChange={set("inquiry_subtitle")} placeholder="İnternet Servis Sağlayıcılığı" data-testid="inquiry-subtitle-input" /></Field>
        </div>
      </Fold>
      <Fold title="KVKK metni" testid="contact-fold-kvkk">
        <I18nInput label="KVKK aydınlatma metni (formdaki onay kutusu)" value={f.kvkk} onChange={set("kvkk")} multiline rows={6} testid="general-kvkk" />
      </Fold>
    </Card>
  );
}

function CommChannelsCard() {
  const { f, set, save, busy } = useSection("communication");
  const toggleArr = (key, val) => {
    const cur = Array.isArray(f[key]) ? f[key] : [];
    const next = cur.includes(val) ? cur.filter((x) => x !== val) : [...cur, val];
    set(key)(next);
  };
  const emailReady = !!f.email_enabled;
  const tgReady = !!f.telegram_enabled;
  const smsReady = !!f.sms_enabled;
  const contactAdmin = Array.isArray(f.contact_admin_channels) ? f.contact_admin_channels : ["email"];
  const contactReply = f.contact_reply_channel || "email";
  const eventAdmin = Array.isArray(f.event_admin_channels) ? f.event_admin_channels : ["telegram"];
  const backup = Array.isArray(f.backup_channels) ? f.backup_channels : [emailReady && "email", tgReady && "telegram"].filter(Boolean);
  const eventInvite = f.event_invite_channel || "email";

  const Opt = ({ group, value, label, hint, disabled, checked, onChange, type = "checkbox", testid }) => (
    <label
      className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
        checked ? "border-amber-400 bg-amber-50" : "border-stone-200 bg-white hover:border-stone-300"
      } ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
      data-testid={testid}
    >
      <input
        type={type}
        name={group}
        value={value}
        checked={checked}
        onChange={() => !disabled && onChange()}
        disabled={disabled}
        className="mt-0.5 w-4 h-4 accent-amber-600"
      />
      <div className="min-w-0">
        <div className="text-sm font-medium text-stone-800">{label}</div>
        <div className="text-[11px] text-stone-500 leading-snug">{hint}</div>
      </div>
    </label>
  );

  return (
    <Card
      title="İletişim ayarları"
      sub="Form doldurulduğunda ve takvimden randevu paylaşıldığında hangi kanalların kullanılacağını belirleyin."
      onSave={save}
      busy={busy}
      testid="comm-channels-card"
    >
      <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-3 space-y-2" data-testid="comm-channels-ready">
        <div className="text-xs font-semibold text-stone-700 flex items-center gap-2">
          Kanalların durumu
          <span className="text-stone-400 font-normal">(aşağıdaki seçimlerin aktif olabilmesi için ilgili kanalın açık olması gerekir)</span>
        </div>
        <div className="grid sm:grid-cols-3 gap-2">
          <label className="flex items-center gap-2 bg-white border border-stone-200 rounded-md p-2 text-sm">
            <Switch checked={!!f.email_enabled} onCheckedChange={set("email_enabled")} data-testid="toggle-email-enabled" />
            <span className="font-medium">E-posta</span>
            <span className="text-[11px] text-stone-500 ml-auto">{f.notify_email ? "✓ hazır" : "notify_email boş"}</span>
          </label>
          <label className="flex items-center gap-2 bg-white border border-stone-200 rounded-md p-2 text-sm">
            <Switch checked={!!f.telegram_enabled} onCheckedChange={set("telegram_enabled")} data-testid="toggle-telegram-enabled" />
            <span className="font-medium">Telegram</span>
            <span className="text-[11px] text-stone-500 ml-auto">{f.telegram_token ? "✓ bot bağlı" : "bot yok"}</span>
          </label>
          <label className="flex items-center gap-2 bg-white border border-stone-200 rounded-md p-2 text-sm">
            <Switch checked={!!f.sms_enabled} onCheckedChange={set("sms_enabled")} data-testid="toggle-sms-enabled" />
            <span className="font-medium">SMS (Telsim)</span>
            <span className="text-[11px] text-stone-500 ml-auto">{f.telsim_username ? "✓ hazır" : "giriş yok"}</span>
          </label>
        </div>
      </div>

      {/* Contact form */}
      <Fold title="İletişim formu bildirimleri" testid="comm-channels-fold-contact" defaultOpen>
        <div>
          <div className="text-xs font-semibold text-stone-700 mb-2">Bana bildirim nereden gelsin? <span className="text-stone-400 font-normal">(birden fazla seçilebilir)</span></div>
          <div className="grid sm:grid-cols-3 gap-2">
            <Opt group="contact-admin" value="email" label="E-posta" hint={emailReady ? "notify_email adresine düşer" : "Yukarıdaki ‘E-posta’ anahtarını açın"} disabled={!emailReady} checked={contactAdmin.includes("email")} onChange={() => toggleArr("contact_admin_channels", "email")} testid="contact-admin-email" />
            <Opt group="contact-admin" value="telegram" label="Telegram" hint={tgReady ? "Bot sohbetine düşer" : "Yukarıdaki ‘Telegram’ anahtarını açın"} disabled={!tgReady} checked={contactAdmin.includes("telegram")} onChange={() => toggleArr("contact_admin_channels", "telegram")} testid="contact-admin-telegram" />
            <Opt group="contact-admin" value="sms" label="SMS" hint={smsReady ? `Yönetici numarasına Telsim SMS` : "Yukarıdaki ‘SMS’ anahtarını açın"} disabled={!smsReady} checked={contactAdmin.includes("sms")} onChange={() => toggleArr("contact_admin_channels", "sms")} testid="contact-admin-sms" />
          </div>
          {contactAdmin.includes("sms") && (
            <div className="mt-3 grid sm:grid-cols-2 gap-3">
              <Field label="Yönetici SMS numarası" hint="05XX XXX XX XX veya +90 ile uluslararası">
                <Input value={f.admin_sms_phone || ""} onChange={set("admin_sms_phone")} placeholder="05421234567" data-testid="comm-admin-sms-phone" />
              </Field>
            </div>
          )}
        </div>

        <div className="pt-4 border-t border-stone-100">
          <div className="text-xs font-semibold text-stone-700 mb-2">Form dolduran kişiye otomatik cevap nasıl gitsin?</div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-2">
            <Opt group="contact-reply" value="none" label="Gönderme" hint="Sessiz al, kimseye yanıt dönme" checked={contactReply === "none"} onChange={() => set("contact_reply_channel")("none")} type="radio" testid="contact-reply-none" />
            <Opt group="contact-reply" value="email" label="Sadece e-posta" hint="İletişim formu otomatik cevap şablonu gider" disabled={!emailReady} checked={contactReply === "email"} onChange={() => set("contact_reply_channel")("email")} type="radio" testid="contact-reply-email" />
            <Opt group="contact-reply" value="sms" label="Sadece SMS" hint="Telefon numarası varsa Telsim SMS atılır" disabled={!smsReady} checked={contactReply === "sms"} onChange={() => set("contact_reply_channel")("sms")} type="radio" testid="contact-reply-sms" />
            <Opt group="contact-reply" value="both" label="İkisi birden" hint="E-posta + telefon varsa SMS" disabled={!emailReady && !smsReady} checked={contactReply === "both"} onChange={() => set("contact_reply_channel")("both")} type="radio" testid="contact-reply-both" />
          </div>
          <p className="text-[11px] text-stone-500 mt-2">Metni "İletişim ayarları → Şablonlar" altındaki ilgili şablonda düzenleyebilirsiniz.</p>
        </div>
      </Fold>

      {/* Calendar event */}
      <Fold title="Takvim randevu bildirimleri" testid="comm-channels-fold-event" defaultOpen>
        <div>
          <div className="text-xs font-semibold text-stone-700 mb-2">Yaklaşan randevular bana nereden hatırlatılsın? <span className="text-stone-400 font-normal">(yeni etkinliklere varsayılan)</span></div>
          <div className="grid sm:grid-cols-3 gap-2">
            <Opt group="event-admin" value="email" label="E-posta" hint={emailReady ? "notify_email adresine düşer" : "Yukarıdaki ‘E-posta’ anahtarını açın"} disabled={!emailReady} checked={eventAdmin.includes("email")} onChange={() => toggleArr("event_admin_channels", "email")} testid="event-admin-email" />
            <Opt group="event-admin" value="telegram" label="Telegram" hint={tgReady ? "Bot sohbetine düşer" : "Yukarıdaki ‘Telegram’ anahtarını açın"} disabled={!tgReady} checked={eventAdmin.includes("telegram")} onChange={() => toggleArr("event_admin_channels", "telegram")} testid="event-admin-telegram" />
            <Opt group="event-admin" value="sms" label="SMS" hint={smsReady ? "Yönetici numarasına Telsim SMS" : "Yukarıdaki ‘SMS’ anahtarını açın"} disabled={!smsReady} checked={eventAdmin.includes("sms")} onChange={() => toggleArr("event_admin_channels", "sms")} testid="event-admin-sms" />
          </div>
        </div>

        <div className="pt-4 border-t border-stone-100">
          <div className="text-xs font-semibold text-stone-700 mb-2">Randevu daveti karşı tarafa nasıl gitsin?</div>
          <div className="grid sm:grid-cols-3 gap-2">
            <Opt group="event-invite" value="email" label="Sadece e-posta" hint="Olağan davetiye e-postası" disabled={!emailReady} checked={eventInvite === "email"} onChange={() => set("event_invite_channel")("email")} type="radio" testid="event-invite-email" />
            <Opt group="event-invite" value="sms" label="Sadece SMS" hint="Takvime ekle bağlantılı kısa SMS" disabled={!smsReady} checked={eventInvite === "sms"} onChange={() => set("event_invite_channel")("sms")} type="radio" testid="event-invite-sms" />
            <Opt group="event-invite" value="both" label="İkisi birden" hint="Hem e-posta hem SMS gönder" disabled={!emailReady && !smsReady} checked={eventInvite === "both"} onChange={() => set("event_invite_channel")("both")} type="radio" testid="event-invite-both" />
          </div>
          <p className="text-[11px] text-stone-500 mt-2">SMS metnini "Şablonlar → Takvim davetiye şablonu" altındaki SMS daveti alanından düzenleyin.</p>
        </div>
      </Fold>

      {/* Backup notifications */}
      <Fold title="Yedekleme bildirimleri" meta="Site yedeği hangi kanallara gönderilsin?" testid="comm-channels-fold-backup" defaultOpen>
        <div>
          <div className="text-xs font-semibold text-stone-700 mb-2">Yedek ZIP bana nereden gelsin? <span className="text-stone-400 font-normal">(birden fazla seçilebilir)</span></div>
          <div className="grid sm:grid-cols-2 gap-2">
            <Opt group="backup" value="email" label="E-posta" hint={emailReady ? "notify_email adresine düşer; 15 MB üstündeyse indirme linki" : "Yukarıdaki ‘E-posta’ anahtarını açın"} disabled={!emailReady} checked={backup.includes("email")} onChange={() => toggleArr("backup_channels", "email")} testid="backup-ch-email" />
            <Opt group="backup" value="telegram" label="Telegram" hint={tgReady ? "Bot sohbetine dosya olarak düşer (≤20 MB)" : "Yukarıdaki ‘Telegram’ anahtarını açın"} disabled={!tgReady} checked={backup.includes("telegram")} onChange={() => toggleArr("backup_channels", "telegram")} testid="backup-ch-telegram" />
          </div>
          <p className="text-[11px] text-stone-500 mt-2">Yedek ayrıca her zaman sunucuda saklanır; <strong>Yedekleme</strong> sekmesinden indirebilirsiniz. Periyot ve saklama sayısı oradan ayarlanır.</p>
        </div>
      </Fold>
    </Card>
  );
}

export function ContactTab() {
  return (
    <>
      <CommChannelsCard />
      <PushSettings />
      <Card title="Şablonlar" sub="Takvim davetiyesi ve iletişim formu otomatik cevap şablonlarınızı buradan düzenleyin." testid="templates-card">
        <CommTemplates />
      </Card>
    </>
  );
}

function SiteContactTab() {
  return <ContactArea />;
}

function TelegramWizard({ onFillCommunication }) {
  const [step, setStep] = useState(1);
  const [token, setToken] = useState("");
  const [bot, setBot] = useState(null); // { username, first_name }
  const [chat, setChat] = useState(null); // { chat_id, first_name }
  const [busy, setBusy] = useState(false);

  const validate = async () => {
    if (!token.trim()) return toast.error("Önce token'ı yapıştırın");
    setBusy(true);
    try {
      const { data } = await api.post("/admin/telegram/validate", { token });
      setBot({ username: data.username, first_name: data.first_name });
      setStep(3);
      toast.success(`Bot doğrulandı: @${data.username}`);
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  const discoverChat = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/telegram/discover-chat", { token });
      if (data.ok) {
        setChat(data);
        setStep(5);
        toast.success(`Chat ID bulundu: ${data.chat_id}`);
      } else {
        toast.message("Henüz mesaj görünmüyor — bot'a bir /start yazıp tekrar deneyin");
      }
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  const finish = () => {
    onFillCommunication({ token, chat_id: chat.chat_id });
    toast.success("Bilgiler Bildirimler alanına aktarıldı — şimdi 'Kaydet'e basın");
    setStep(1); setToken(""); setBot(null); setChat(null);
  };

  const Step = ({ n, label, done }) => (
    <div className={`flex items-center gap-2 ${step === n ? "text-stone-900" : done ? "text-emerald-600" : "text-stone-400"}`}>
      <span className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold ${
        done ? "bg-emerald-500 text-white" : step === n ? "bg-stone-900 text-white" : "bg-stone-200 text-stone-500"
      }`}>{done ? <Check size={12} /> : n}</span>
      <span className="text-xs sm:text-sm font-medium">{label}</span>
    </div>
  );

  return (
    <CardCollapse
      testid="telegram-wizard"
      icon={<Bot size={16} />}
      title="Telegram Botu — Adım adım kurulum"
      description="Yarı-otomatik sihirbaz. BotFather'ı açar, token'ı doğrular, chat ID'nizi kendisi bulur."
      chip={<span className="text-[11px] uppercase tracking-widest text-stone-500 font-semibold">Yönetimli</span>}
    >
      {/* Steps indicator */}
      <div className="flex flex-wrap items-center gap-x-5 gap-y-2 border-y py-3">
        <Step n={1} label="BotFather'ı aç" done={step > 1} />
        <ArrowRight size={12} className="text-stone-300" aria-hidden />
        <Step n={2} label="Token'ı yapıştır" done={step > 2} />
        <ArrowRight size={12} className="text-stone-300" aria-hidden />
        <Step n={3} label="Bot'a mesaj at" done={step > 3} />
        <ArrowRight size={12} className="text-stone-300" aria-hidden />
        <Step n={4} label="Chat ID bul" done={step > 4} />
        <ArrowRight size={12} className="text-stone-300" aria-hidden />
        <Step n={5} label="Kaydet" done={step > 5} />
      </div>

      {/* Step bodies */}
      {step === 1 && (
        <div className="space-y-3">
          <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2.5 text-sm text-amber-900">
            <strong>Önemli:</strong> Bu sihirbaz <strong>yeni bir bot oluşturur</strong>. Mevcut botlarına hiç dokunmaz. BotFather'da aşağıdaki komutun <strong>aynısını</strong> yaz; başka komut (ör. <code className="bg-amber-100 border px-1 rounded">/mybots</code>, <code className="bg-amber-100 border px-1 rounded">/token</code>) yazarsan BotFather sana <em>eski bot listesi</em> gösterir — o ekrandan vazgeç ve <code className="bg-amber-100 border px-1 rounded">/newbot</code> yaz.
          </div>
          <p className="text-sm text-stone-700">1) BotFather'ı aç. 2) <code className="bg-stone-100 border px-1 rounded">/newbot</code> yaz (aşağıdaki düğmeyle kopyalayabilirsin). 3) BotFather istediği yeni bot adını ve <strong>_bot</strong> ile biten kullanıcı adını belirle. 4) Sana verdiği token'ı kopyala.</p>
          <div className="flex flex-wrap items-center gap-2">
            <a href="https://t.me/BotFather" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md bg-sky-600 text-white text-sm font-medium hover:bg-sky-700" data-testid="telegram-wizard-open-botfather">
              <ExternalLink size={13} /> @BotFather'ı Aç
            </a>
            <button
              type="button"
              onClick={() => { navigator.clipboard.writeText("/newbot").then(() => toast.success('"/newbot" kopyalandı — BotFather sohbetine yapıştır')).catch(() => toast.message("/newbot")); }}
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-stone-300 bg-white text-stone-800 text-sm font-medium hover:bg-stone-50"
              data-testid="telegram-wizard-copy-newbot"
            >
              📋 <code className="font-mono">/newbot</code> komutunu kopyala
            </button>
            <button type="button" onClick={() => setStep(2)} className="text-sm text-stone-600 hover:text-stone-900 underline-offset-2 hover:underline" data-testid="telegram-wizard-step1-next">Token'ım hazır →</button>
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-3">
          <p className="text-sm text-stone-700">BotFather'ın verdiği token'ı buraya yapıştır. Doğrula'ya basınca Telegram'a sorup bot adını göstereceğim.</p>
          <div className="flex items-center gap-2">
            <input
              value={token}
              onChange={(e) => setToken(e.target.value)}
              placeholder="123456789:AAE…"
              className="flex-1 border rounded-md h-9 px-3 text-sm font-mono"
              data-testid="telegram-wizard-token"
              autoFocus
            />
            <button type="button" onClick={validate} disabled={busy || !token.trim()} className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md bg-stone-900 text-white text-sm font-medium disabled:opacity-50" data-testid="telegram-wizard-validate">
              {busy ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />} Doğrula
            </button>
          </div>
          <button type="button" onClick={() => setStep(1)} className="text-xs text-stone-500 hover:underline">← Geri</button>
        </div>
      )}

      {step === 3 && bot && (
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
            <Check size={14} /> Bot doğrulandı: <strong>{bot.first_name}</strong> <span className="text-emerald-600 font-mono">@{bot.username}</span>
          </div>
          <p className="text-sm text-stone-700">Şimdi botunu Telegram'da aç ve <code className="bg-stone-100 border px-1 rounded">/start</code> yaz (ya da herhangi bir şey). Bu bir kez yeterli — sana sohbet kimliğini vereceğim.</p>
          <div className="flex items-center gap-2">
            <a href={`https://t.me/${bot.username}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md bg-sky-600 text-white text-sm font-medium hover:bg-sky-700" data-testid="telegram-wizard-open-bot">
              <ExternalLink size={13} /> @{bot.username} sohbetini aç
            </a>
            <button type="button" onClick={() => setStep(4)} className="text-sm text-stone-600 hover:text-stone-900 underline-offset-2 hover:underline" data-testid="telegram-wizard-step3-next">Mesajımı attım →</button>
          </div>
        </div>
      )}

      {step === 4 && (
        <div className="space-y-3">
          <p className="text-sm text-stone-700">Bot'a mesaj yolladıysan "Chat ID'mi bul" düğmesine bas. Telegram'daki son mesajını alıp sohbet kimliğini çıkaracağım.</p>
          <div className="flex items-center gap-2">
            <button type="button" onClick={discoverChat} disabled={busy} className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md bg-stone-900 text-white text-sm font-medium disabled:opacity-50" data-testid="telegram-wizard-discover">
              {busy ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />} Chat ID'mi bul
            </button>
            <button type="button" onClick={() => setStep(3)} className="text-xs text-stone-500 hover:underline">← Geri</button>
          </div>
        </div>
      )}

      {step === 5 && chat && (
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
            <Check size={14} /> Chat ID bulundu: <strong className="font-mono">{chat.chat_id}</strong>
            {chat.first_name && <span className="text-stone-600">({chat.first_name})</span>}
          </div>
          <p className="text-sm text-stone-700">Şimdi "Alanlara doldur ve devam"a basınca aşağıdaki <strong>İletişim bildirimleri</strong> kartına token ve chat ID otomatik yazılacak. Oradan <strong>Kaydet</strong>'e basman yeterli.</p>
          <div className="flex items-center gap-2">
            <button type="button" onClick={finish} className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700" data-testid="telegram-wizard-finish">
              <ArrowRight size={13} /> Alanlara doldur ve devam
            </button>
            <button type="button" onClick={() => { setStep(1); setToken(""); setBot(null); setChat(null); }} className="text-xs text-stone-500 hover:underline">Baştan başla</button>
          </div>
        </div>
      )}
    </CardCollapse>
  );
}

function ResendCard() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["admin", "resend"], queryFn: () => api.get("/admin/integrations/resend").then((r) => r.data) });
  const [f, setF] = useState({ api_key: "", from_email: "", enabled: false });
  const [testTo, setTestTo] = useState("");
  const [busy, setBusy] = useState(false);
  const [verify, setVerify] = useState(null); // {valid, domains, reason}
  const [verifying, setVerifying] = useState(false);
  useEffect(() => { if (data) setF({ api_key: "", from_email: data.from_email || "", enabled: !!data.enabled }); }, [data]);

  const configured = !!data?.api_key_configured;
  const last4 = data?.api_key_last4 || "";

  const runVerify = async () => {
    if (!configured) return;
    setVerifying(true);
    try {
      const r = await api.get("/admin/integrations/resend/verify");
      setVerify(r.data);
    } catch (e) {
      setVerify({ valid: false, reason: "error", domains: [] });
    }
    setVerifying(false);
  };
  // auto verify when the card loads with a saved key
  useEffect(() => { if (configured) runVerify(); /* eslint-disable-next-line */ }, [configured]);

  const save = async (overrides = {}) => {
    setBusy(true);
    try {
      const payload = { ...f, ...overrides };
      // Yeni ve geçerli görünümlü bir anahtar yapıştırıldıysa aktifliği otomatik aç
      if (payload.api_key && payload.api_key.startsWith("re_") && !("enabled" in overrides)) {
        payload.enabled = true;
      }
      await api.put("/admin/integrations/resend", payload);
      toast.success("Resend ayarları kaydedildi");
      setF((p) => ({ ...p, api_key: "", enabled: !!payload.enabled }));
      await qc.invalidateQueries({ queryKey: ["admin", "resend"] });
      // kaydettikten sonra otomatik doğrula
      setTimeout(runVerify, 400);
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const quickToggle = async () => {
    await save({ api_key: "", enabled: !data?.enabled });
  };
  const test = async () => {
    if (!/^.+@.+\..+$/.test(testTo.trim())) return toast.error("Test alıcısı için geçerli e-posta gir");
    setBusy(true);
    try {
      await api.post("/admin/integrations/resend/test", { to: testTo.trim() });
      toast.success(`Test e-postası ${testTo} adresine gönderildi`);
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  // Banner state
  let bannerTone = "stone"; let bannerIcon = AlertTriangle; let bannerTitle = "Henüz yapılandırılmadı"; let bannerSub = "API anahtarını ve gönderici adresi gir, aç ve kaydet.";
  if (configured && verify?.valid && data?.enabled) {
    bannerTone = "emerald"; bannerIcon = CheckCircle2;
    bannerTitle = `Aktif ve çalışıyor${last4 ? ` · re_••••${last4}` : ""}`;
    bannerSub = verify.scope === "sending"
      ? "Davetiye ve bildirim e-postaları Resend üzerinden gidiyor. (Sending-only anahtar — domain listesi için Full access gerekir.)"
      : "Davetiye ve bildirim e-postaları Resend üzerinden gidiyor.";
  } else if (configured && verify?.valid && !data?.enabled) {
    bannerTone = "amber"; bannerIcon = Power;
    bannerTitle = `Anahtar doğrulandı ama pasif${last4 ? ` · re_••••${last4}` : ""}`;
    bannerSub = verify.scope === "sending"
      ? "Sending-only anahtar geçerli. Tek tıkla aktif et — Resend üzerinden gönderim başlar."
      : "Tek tıkla aktif et — Emergent yönetimli gönderici yerine Resend devreye girsin.";
  } else if (configured && verify && !verify.valid) {
    bannerTone = "rose"; bannerIcon = AlertTriangle;
    const reasons = { unauthorized: "Anahtar geçersiz veya iptal edilmiş.", network: "Resend'e ulaşılamadı — tekrar dene.", no_key: "Anahtar bulunamadı." };
    bannerTitle = `Kayıtlı ama doğrulanamadı${last4 ? ` · re_••••${last4}` : ""}`;
    bannerSub = reasons[verify.reason] || "Resend yanıt vermedi. Anahtarı güncelle veya tekrar dene.";
  } else if (configured && !verify) {
    bannerTone = "stone"; bannerIcon = Loader2;
    bannerTitle = `Anahtar kayıtlı${last4 ? ` · re_••••${last4}` : ""}`;
    bannerSub = "Resend ile doğrulanıyor…";
  }
  const toneMap = {
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-900",
    amber: "bg-amber-50 border-amber-200 text-amber-900",
    rose: "bg-rose-50 border-rose-200 text-rose-900",
    stone: "bg-stone-50 border-stone-200 text-stone-700",
  };
  const Icon = bannerIcon;

  const verifiedDomains = (verify?.domains || []).filter((d) => (d.status || "").toLowerCase() === "verified");
  const chooseFromDomain = (name) => setF((p) => ({ ...p, from_email: p.from_email && p.from_email.includes("@") ? `${p.from_email.split("@")[0]}@${name}` : `no-reply@${name}` }));

  const chipResend = (
    <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
      {configured && (
        <button type="button" onClick={runVerify} disabled={verifying} className="inline-flex items-center gap-1 text-xs text-stone-600 hover:text-stone-900 disabled:opacity-50" data-testid="resend-verify">
          <RefreshCw size={12} className={verifying ? "animate-spin" : ""} /> Yeniden doğrula
        </button>
      )}
      <span className={`text-[11px] uppercase tracking-widest font-semibold px-2 py-1 rounded ${configured && data?.enabled && verify?.valid ? "bg-emerald-100 text-emerald-700" : configured ? "bg-amber-100 text-amber-700" : "bg-stone-100 text-stone-600"}`}>
        {configured && data?.enabled && verify?.valid ? "Aktif" : configured ? "Yapılandırıldı" : "Kapalı"}
      </span>
    </div>
  );

  return (
    <CardCollapse
      testid="integration-resend"
      icon={<Send size={16} className="text-amber-700" />}
      title="Resend (kendi e-posta sağlayıcın)"
      description="Davetiye ve bildirim e-postaları Resend üzerinden gider. Boş bırakırsan Emergent yönetimli gönderici devreye girer."
      chip={chipResend}
    >
      {/* Prominent status banner */}
      <div className={`flex items-start gap-3 border rounded-lg p-3 ${toneMap[bannerTone]}`} data-testid="resend-status-banner">
        <Icon size={18} className={`mt-0.5 shrink-0 ${bannerIcon === Loader2 ? "animate-spin" : ""}`} />
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-sm" data-testid="resend-status-title">{bannerTitle}</div>
          <div className="text-xs opacity-90 mt-0.5">{bannerSub}</div>
        </div>
        {configured && verify?.valid && !data?.enabled && (
          <button type="button" onClick={quickToggle} disabled={busy} className="shrink-0 inline-flex items-center gap-1 h-8 px-3 rounded-md bg-amber-600 text-white text-xs font-semibold hover:bg-amber-700 disabled:opacity-50" data-testid="resend-quick-enable">
            <Power size={12} /> Aktif et
          </button>
        )}
        {configured && data?.enabled && (
          <button type="button" onClick={quickToggle} disabled={busy} className="shrink-0 text-xs text-emerald-700 hover:underline" data-testid="resend-quick-disable">
            Pasif yap
          </button>
        )}
      </div>

      {/* Verified domains */}
      {configured && verify?.valid && verify.scope === "full" && (
        <div className="text-xs" data-testid="resend-domains">
          <div className="font-semibold text-stone-700 mb-1">Resend hesabındaki domainler</div>
          {verify.domains.length === 0 ? (
            <div className="text-stone-500">Henüz domain eklememişsin. <a href="https://resend.com/domains" target="_blank" rel="noopener noreferrer" className="underline">resend.com/domains</a> üzerinden ekle.</div>
          ) : (
            <ul className="space-y-1">
              {verify.domains.map((d) => (
                <li key={d.name} className="flex items-center gap-2">
                  <span className={`inline-block w-1.5 h-1.5 rounded-full ${(d.status || "").toLowerCase() === "verified" ? "bg-emerald-500" : "bg-amber-500"}`} />
                  <code className="font-mono">{d.name}</code>
                  <span className="text-stone-500">{d.status || "pending"}</span>
                  {(d.status || "").toLowerCase() === "verified" && (
                    <button type="button" onClick={() => chooseFromDomain(d.name)} className="text-amber-700 hover:underline">bu domaini kullan</button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      {configured && verify?.valid && verify.scope === "sending" && (
        <div className="text-xs bg-stone-50 border border-stone-200 p-3 rounded" data-testid="resend-sending-note">
          <div className="font-semibold text-stone-700">Sending-only anahtar</div>
          <div className="text-stone-600 mt-0.5">Bu anahtar yalnızca e-posta gönderir; domain listelemez. Gönderici e-postanı doğrulanmış bir domainden gir (örn. <code>no-reply@olgu.eu</code>). Domain listesi görmek istiyorsan <a href="https://resend.com/api-keys" target="_blank" rel="noopener noreferrer" className="underline">resend.com/api-keys</a>'ten "Full access" anahtar oluştur.</div>
        </div>
      )}

      {/* Setup hint when not configured */}
      {!configured && (
        <ol className="text-xs text-stone-600 list-decimal pl-5 space-y-1 bg-amber-50 border border-amber-200 p-3 rounded-lg">
          <li>resend.com'da hesap oluştur, Domains → domainini doğrula.</li>
          <li>API Keys → Create API Key → full access → kopyala (<code className="bg-amber-100 px-1 rounded">re_...</code>).</li>
          <li>Buraya API anahtarını ve gönderici adresini (örn. <code>no-reply@olgu.eu</code>) yapıştır, aç ve kaydet.</li>
        </ol>
      )}

      <div className="grid md:grid-cols-2 gap-4">
        <Field label="API anahtarı" hint={configured && !f.api_key ? `Kayıtlı: re_••••${last4}. Değiştirmek için yeni anahtarı yapıştır.` : "re_ ile başlar"}>
          <Input type="password" autoComplete="new-password" value={f.api_key} onChange={(e) => setF({ ...f, api_key: e.target.value })} placeholder={configured ? `re_••••${last4}` : "re_abc..."} data-testid="resend-api-key" />
        </Field>
        <Field label="Gönderici e-posta">
          <Input type="email" value={f.from_email} onChange={(e) => setF({ ...f, from_email: e.target.value })} placeholder="no-reply@olgu.eu" data-testid="resend-from-email" />
        </Field>
      </div>
      <label className="flex items-center gap-3 text-sm font-medium">
        <input type="checkbox" checked={!!f.enabled} onChange={(e) => setF({ ...f, enabled: e.target.checked })} className="w-4 h-4 accent-amber-600" data-testid="resend-enabled" />
        Resend üzerinden gönder (kapalıysa Emergent yönetimli gönderici devreye girer)
      </label>
      <div className="flex items-center gap-2 pt-2 border-t flex-wrap">
        <Button type="button" onClick={() => save()} disabled={busy} data-testid="resend-save"><Save size={14} className="mr-1" />Kaydet ve doğrula</Button>
        {configured && verify?.valid && (
          <>
            <Input type="email" value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="test@adresim.com" className="max-w-xs" data-testid="resend-test-to" />
            <Button type="button" variant="outline" onClick={test} disabled={busy || !testTo || !data?.enabled} data-testid="resend-test"><Send size={13} className="mr-1" />Test gönder</Button>
            {!data?.enabled && <span className="text-xs text-stone-500">Test için önce aktif et</span>}
          </>
        )}
      </div>
    </CardCollapse>
  );
}

function TelsimCard() {
  const qc = useQueryClient();
  const { data: telsim } = useQuery({ queryKey: ["admin", "telsim"], queryFn: () => api.get("/admin/integrations/telsim").then((r) => r.data) });
  const { f, set, save: saveComm, busy: commBusy } = useSection("communication");
  const [creds, setCreds] = useState({ username: "", password: "" });
  const [originator, setOriginator] = useState("");
  const [savingCreds, setSavingCreds] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [testBusy, setTestBusy] = useState(false);
  const [testOut, setTestOut] = useState(null);

  useEffect(() => { if (telsim?.originator !== undefined) setOriginator(telsim.originator || ""); }, [telsim?.originator]);

  const credsConfigured = !!(telsim?.username_configured && telsim?.password_configured);
  const channelReady = credsConfigured && !!originator;
  const smsEnabled = !!f.sms_enabled;

  const saveCreds = async () => {
    if (!creds.username && !telsim?.username_configured) return toast.error("Önce kullanıcı adını girin");
    if (!creds.password && !telsim?.password_configured) return toast.error("Önce şifreyi girin");
    if (!originator) return toast.error("Originator (gönderici adı) gerekli");
    setSavingCreds(true);
    try {
      await api.put("/admin/integrations/telsim", {
        username: creds.username,
        password: creds.password,
        originator,
      });
      toast.success("Telsim kimlik bilgileri kaydedildi");
      setCreds({ username: "", password: "" });
      await qc.invalidateQueries({ queryKey: ["admin", "telsim"] });
    } catch (e) { toast.error(errMsg(e)); }
    setSavingCreds(false);
  };

  const runTest = async () => {
    if (!testTo.trim()) return toast.error("Test numarasını girin (ör. 05421234567)");
    setTestBusy(true);
    setTestOut(null);
    try {
      const payload = {
        to: testTo.trim(),
        text: "Olgu Tansug CMS: Telsim SMS testi. Bu bir test mesajidir.",
        originator,
        username: creds.username,
        password: creds.password,
      };
      const { data } = await api.post("/admin/integrations/telsim/test", payload);
      setTestOut({ ok: true, info: data.info || "Gönderildi" });
      toast.success("Test SMS gönderildi");
    } catch (e) {
      setTestOut({ ok: false, info: errMsg(e) });
      toast.error(errMsg(e));
    }
    setTestBusy(false);
  };

  const chip = (
    <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
      <label className="flex items-center gap-2 text-xs text-stone-600">
        <Switch
          checked={smsEnabled}
          onCheckedChange={(v) => { set("sms_enabled")(v); saveComm({ ...f, sms_enabled: v }); }}
          data-testid="telsim-sms-enabled"
          disabled={!channelReady}
        />
        <span>SMS kanalı aktif</span>
      </label>
      <span className={`text-[11px] uppercase tracking-widest font-semibold px-2 py-1 rounded ${
        smsEnabled && channelReady ? "bg-emerald-100 text-emerald-700" : credsConfigured ? "bg-amber-100 text-amber-700" : "bg-stone-100 text-stone-600"
      }`}>
        {smsEnabled && channelReady ? "Aktif" : credsConfigured ? "Yapılandırıldı" : "Kapalı"}
      </span>
    </div>
  );

  const bannerTone = channelReady ? (smsEnabled ? "emerald" : "amber") : "stone";
  const bannerIcon = channelReady ? CheckCircle2 : AlertTriangle;
  const bannerText = !credsConfigured
    ? "Kullanıcı adı ve şifre eksik — Telsim'den aldığınız API kimlik bilgilerini girin."
    : !originator
      ? "Originator (gönderici adı) eksik — Telsim'de kayıtlı 11 karaktere kadar olan adı girin."
      : smsEnabled
        ? `Telsim yapılandırıldı · Originator: ${originator} · Formlardan otomatik SMS akışı açık.`
        : `Telsim yapılandırıldı · Originator: ${originator} · SMS kanalı pasif — formlardan otomatik SMS gitmez. Test yine de yapılabilir.`;
  const toneMap = {
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-900",
    amber: "bg-amber-50 border-amber-200 text-amber-900",
    stone: "bg-stone-50 border-stone-200 text-stone-700",
  };
  const BIcon = bannerIcon;

  return (
    <CardCollapse
      testid="integration-telsim"
      icon={<MessageSquare size={16} className="text-rose-600" />}
      title="KKTC Telsim SMS (toplusms.kktctelsim.com)"
      description="HTTP/XML SMS gateway. Kimlik bilgileri şifrelenerek saklanır. İletişim formlarından otomatik SMS gönderimini bu kanaldan tetikleyebilirsiniz."
      chip={chip}
    >
      <div className={`flex items-start gap-3 border rounded-lg p-3 ${toneMap[bannerTone]}`} data-testid="telsim-status-banner">
        <BIcon size={18} className="mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0 text-sm">{bannerText}</div>
      </div>

      {!credsConfigured && (
        <ol className="text-xs text-stone-600 list-decimal pl-5 space-y-1 bg-amber-50 border border-amber-200 p-3 rounded-lg">
          <li>Telsim kurumsal müşteri temsilcinizden <strong>HTTP/XML SMS</strong> API kullanıcı adı/şifresini isteyin.</li>
          <li>Panelinizde kayıtlı <strong>Originator</strong> (gönderici adı) değerini öğrenin — en fazla 11 karakter alfanumerik.</li>
          <li>Aşağıya yapıştırın, kaydedin, test numaranıza bir SMS göndererek doğrulayın.</li>
        </ol>
      )}

      <div className="grid md:grid-cols-3 gap-3">
        <Field
          label="Kullanıcı adı"
          hint={telsim?.username_configured ? `Kayıtlı: ••••${telsim.username_last4 || ""} — değiştirmek için yeni ad girin` : "API için verilen kullanıcı adı"}
        >
          <Input
            autoComplete="off"
            value={creds.username}
            onChange={(e) => setCreds({ ...creds, username: e.target.value })}
            placeholder={telsim?.username_configured ? "Değiştirmek için yazın" : "telsim_api_user"}
            data-testid="telsim-username"
          />
        </Field>
        <Field
          label="Şifre"
          hint={telsim?.password_configured ? "Kayıtlı — değiştirmek için yeni şifre girin" : "API için verilen şifre"}
        >
          <Input
            type="password"
            autoComplete="new-password"
            value={creds.password}
            onChange={(e) => setCreds({ ...creds, password: e.target.value })}
            placeholder={telsim?.password_configured ? "••••••••" : "***"}
            data-testid="telsim-password"
          />
        </Field>
        <Field label="Originator (gönderici adı)" hint="Telsim'de kayıtlı 11 karaktere kadar ad">
          <Input
            value={originator}
            onChange={(e) => setOriginator(e.target.value.slice(0, 20))}
            placeholder="OLGUTANSUG"
            maxLength={20}
            data-testid="telsim-originator"
          />
        </Field>
      </div>

      <div className="flex items-center gap-2 pt-1">
        <Button type="button" onClick={saveCreds} disabled={savingCreds} data-testid="telsim-save-creds">
          <Save size={14} className="mr-1" /> {savingCreds ? "Kaydediliyor…" : "Kimlik bilgilerini kaydet"}
        </Button>
        {credsConfigured && <span className="text-xs text-emerald-700">✓ Kimlik bilgileri saklı</span>}
      </div>

      {/* Test SMS */}
      <div className="border rounded-lg p-4 space-y-3 bg-white">
        <div className="text-xs font-semibold text-stone-700">Test SMS gönder</div>
        <p className="text-[11px] text-stone-500">Not: Kanal kapalı olsa bile test gönderilebilir. Kaydettikten sonra çalışır; girdiğiniz yeni kullanıcı adı/şifre varsa onlar kullanılır.</p>
        <div className="flex items-end gap-2 flex-wrap">
          <Field label="Alıcı numara">
            <div className="relative">
              <Smartphone size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
              <Input
                value={testTo}
                onChange={(e) => setTestTo(e.target.value)}
                placeholder="05421234567"
                className="pl-9"
                data-testid="telsim-test-to"
              />
            </div>
          </Field>
          <Button
            type="button"
            variant="outline"
            onClick={runTest}
            disabled={testBusy || !originator || !(credsConfigured || (creds.username && creds.password))}
            data-testid="telsim-test-send"
          >
            {testBusy ? <Loader2 size={14} className="mr-1 animate-spin" /> : <SendIcon size={13} className="mr-1" />}
            Test gönder
          </Button>
        </div>
        {testOut && (
          <div
            className={`text-xs rounded-lg border p-3 ${
              testOut.ok
                ? "bg-emerald-50 border-emerald-200 text-emerald-900"
                : "bg-rose-50 border-rose-200 text-rose-900"
            }`}
            data-testid="telsim-test-result"
          >
            {testOut.ok ? "✓ " : "✗ "}{testOut.info}
          </div>
        )}
      </div>

      {/* Admin notification + submitter auto-reply */}
    </CardCollapse>
  );
}

function Integrations() {
  const [prefill, setPrefill] = useState(null);
  return (
    <div className="space-y-5">
      <TelegramWizard onFillCommunication={(v) => setPrefill(v)} />
      <Communication prefill={prefill} onPrefillConsumed={() => setPrefill(null)} />
      <ResendCard />
      <TelsimCard />
      <GoogleCalendarSync />
      <RecaptchaCard />
      <div className="bg-stone-50 border rounded-xl p-5" data-testid="integration-placeholders">
        <h3 className="font-semibold text-sm mb-2">Yakında eklenecek entegrasyonlar</h3>
        <ul className="text-xs text-stone-600 grid sm:grid-cols-2 gap-2">
          <li>• Google Analytics (ziyaretçi raporu)</li>
          <li>• Google Search Console doğrulama</li>
          <li>• Cloudflare arkasında çalışma modu</li>
          <li>• Instagram / YouTube akışı</li>
        </ul>
      </div>
    </div>
  );
}

function Backup() {
  return (
    <div className="space-y-5">
      <GoogleDriveBackup />
      <DriveBackupCard />
      <FullDbTransfer />
      <MigrationTool />
    </div>
  );
}

function Communication({ prefill, onPrefillConsumed }) {
  const { f, set, save, busy } = useSection("communication");
  const [token, setToken] = useState("");
  useEffect(() => {
    if (!prefill) return;
    if (prefill.token) setToken(prefill.token);
    if (prefill.chat_id) set("telegram_chat_id")(prefill.chat_id);
    if (prefill.token || prefill.chat_id) set("telegram_enabled")(true);
    onPrefillConsumed?.();
    // scroll to card so user sees the filled fields
    setTimeout(() => {
      document.querySelector('[data-testid="settings-communication"]')?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);
  }, [prefill]);
  const test = async () => {
    try {
      const { data } = await api.post("/admin/notify/test");
      toast.message(`E-posta: ${data.email === null ? "ayarlı değil" : data.email ? "gönderildi" : "başarısız"} · Telegram: ${data.telegram === null ? "ayarlı değil" : data.telegram ? "gönderildi" : "başarısız"}`);
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <>
    <Card title="İletişim bildirimleri" sub="Yeni iletişim veya gönüllü mesajı geldiğinde bildirim alın." onSave={async () => { await save({ ...f, telegram_token: token }); setToken(""); }} busy={busy} testid="settings-communication" defaultOpen={!!prefill}>
      <div className="border rounded-lg p-4 space-y-4">
        <label className="flex items-center gap-3 font-medium text-sm"><Switch checked={!!f.email_enabled} onCheckedChange={set("email_enabled")} data-testid="email-enabled-switch" />E-posta bildirimi (Emergent yönetimli Resend)</label>
        <div className="grid md:grid-cols-3 gap-4">
          <Field label="Bildirimlerin gideceği adres"><Input type="email" value={f.notify_email || ""} onChange={set("notify_email")} data-testid="notify-email-input" /></Field>
          <Field label="Gönderen adı"><Input value={f.from_name || ""} onChange={set("from_name")} data-testid="from-name-input" /></Field>
          <Field label="Yanıt adresi (Reply-To)"><Input type="email" value={f.reply_to || ""} onChange={set("reply_to")} data-testid="reply-to-input" /></Field>
        </div>
        <p className="text-xs text-stone-400">Gönderen e-posta adresi platform tarafından sabittir; gönderim başına Emergent kredisi harcanır.</p>
      </div>
      <div className="border rounded-lg p-4 space-y-4">
        <label className="flex items-center gap-3 font-medium text-sm"><Switch checked={!!f.telegram_enabled} onCheckedChange={set("telegram_enabled")} data-testid="telegram-enabled-switch" />Telegram bildirimi</label>
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Bot token" hint={f.telegram_token ? `Kayıtlı: ${f.telegram_token}` : "@BotFather ile bot oluşturup token'ı alın"}>
            <Input type="password" autoComplete="new-password" value={token} onChange={(e) => setToken(e.target.value)} data-testid="telegram-token-input" />
          </Field>
          <Field label="Sohbet (chat) ID" hint="Bota mesaj atıp @userinfobot ile ID'nizi öğrenin"><Input value={f.telegram_chat_id || ""} onChange={set("telegram_chat_id")} data-testid="telegram-chat-input" /></Field>
        </div>
      </div>
      <Button type="button" variant="outline" onClick={test} data-testid="notify-test-button"><Send size={14} className="mr-1" />Test bildirimi gönder (önce kaydedin)</Button>
    </Card>
    </>
  );
}

function Redirects() {
  const qc = useQueryClient();
  const { data = [] } = useQuery({ queryKey: ["admin", "redirects"], queryFn: () => api.get("/admin/redirects").then((r) => r.data) });
  const [n, setN] = useState({ from: "", to: "" });
  const add = async (e) => {
    e.preventDefault();
    try { await api.post("/admin/redirects", n); setN({ from: "", to: "" }); qc.invalidateQueries({ queryKey: ["admin", "redirects"] }); } catch (er) { toast.error(errMsg(er)); }
  };
  const del = async (id) => { await api.delete(`/admin/redirects/${id}`); qc.invalidateQueries({ queryKey: ["admin", "redirects"] }); };
  return (
    <Card title="Yönlendirmeler" sub="Adres değişince otomatik eklenir; elle de ekleyebilirsiniz." testid="settings-redirects">
      <form onSubmit={add} className="grid md:grid-cols-[1fr_1fr_auto] gap-3">
        <Input placeholder="/eski-adres" value={n.from} onChange={(e) => setN({ ...n, from: e.target.value })} data-testid="redirect-from" />
        <Input placeholder="/yeni-adres" value={n.to} onChange={(e) => setN({ ...n, to: e.target.value })} data-testid="redirect-to" />
        <Button data-testid="redirect-add"><Plus size={15} /></Button>
      </form>
      <div className="divide-y border rounded-lg text-sm">
        {data.map((r) => (
          <div key={r.id} className="flex items-center gap-3 px-3 py-2" data-testid={`redirect-row-${r.id}`}>
            <code className="flex-1 truncate">{r.from} → {r.to}</code>
            {r.auto && <span className="text-[10px] text-stone-400 uppercase">otomatik</span>}
            <Button size="icon" variant="ghost" onClick={() => del(r.id)}><Trash2 size={14} className="text-red-500" /></Button>
          </div>
        ))}
        {!data.length && <p className="px-3 py-3 text-stone-400">Yönlendirme yok.</p>}
      </div>
    </Card>
  );
}

function SEO() {
  const { f, set, save, busy, setSave } = useSection("seo");
  const [robotsBusy, setRobotsBusy] = useState(false);
  const [robotsTone, setRobotsTone] = useState("standart");
  const siteBase = (f.site_url || "").replace(/\/+$/, "") || "https://olgu.eu";
  const generateRobots = async () => {
    setRobotsBusy(true);
    try {
      const { data } = await api.post("/admin/seo/generate-robots", { tone: robotsTone });
      const cleaned = (data.text || "")
        .split("\n")
        .filter((l) => !/^(User-agent|Sitemap)\s*:/i.test(l.trim()) && !/^Disallow:\s*\/admin$/i.test(l.trim()) && !/^Disallow:\s*\/api\/admin$/i.test(l.trim()))
        .join("\n")
        .trim();
      set("robots_extra")(cleaned);
      toast.success("robots.txt oluşturuldu — gözden geçirip Kaydet'e basın");
    } catch (e) { toast.error(errMsg(e)); }
    setRobotsBusy(false);
  };
  const gaValid = !f.ga_id || /^G-[A-Z0-9]{4,12}$/.test(f.ga_id || "");
  const gscValid = !f.gsc_code || /^[A-Za-z0-9_\-]{20,100}$/.test(f.gsc_code || "");
  return (
    <>
      <Card title="SEO" sub="Her sayfa için başlık, açıklama, paylaşım kartları, kanonik adres, hreflang, yapısal veri ve sitemap otomatik üretilir." onSave={save} busy={busy} testid="settings-seo">
        <div className="grid md:grid-cols-2 gap-5">
          <Field label="Başlık şablonu" hint="%s = içerik başlığı, %site = site adı"><Input value={f.title_template || ""} onChange={set("title_template")} data-testid="seo-template" /></Field>
          <Field label="Site adresi (alan adı)" hint="Örn: https://adiniz.com — sitemap için"><Input value={f.site_url || ""} onChange={set("site_url")} data-testid="seo-site-url" /></Field>
        </div>
        <I18nInput label="Varsayılan açıklama" value={f.description} onChange={set("description")} multiline rows={2} maxLength={160} testid="seo-default-description" />
        <ImageUpload label="Varsayılan paylaşım görseli" value={f.og_image} onChange={setSave("og_image")} testid="seo-og-image" alt="paylaşım görseli" />

        <div className="border rounded-lg p-4 space-y-3" data-testid="seo-ga-card">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <p className="font-semibold text-sm">📊 Google Analytics</p>
            <a href="https://analytics.google.com/analytics/web/#/a0p/analytics-create-flow?nextPage=accounts" target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 underline" data-testid="seo-ga-create-link">+ Yeni GA4 mülkü oluştur ↗</a>
          </div>
          <Field label="Ölçüm kimliği" hint="Google'ın verdiği tam gtag.js scriptini yapıştırabilir ya da sadece G-XXXXXXX kodunu girebilirsiniz — otomatik ayıklanır.">
            <Input
              value={f.ga_id || ""}
              onChange={(e) => {
                const raw = e.target.value || "";
                const m = raw.match(/G-[A-Z0-9]{4,12}/);
                set("ga_id")(m ? m[0] : raw.trim());
              }}
              placeholder="G-ABCD1234 veya scripti yapıştırın"
              data-testid="seo-ga"
            />
          </Field>
          {!gaValid && <p className="text-xs text-red-600" data-testid="seo-ga-invalid">⚠ Biçim hatalı — G- ile başlayıp 4-12 karakter olmalı</p>}
          {f.ga_id && gaValid && <p className="text-xs text-emerald-700" data-testid="seo-ga-ok">✓ Geçerli ölçüm kimliği · site yüklendiğinde gtag.js otomatik aktifleşir</p>}
        </div>

        <div className="border rounded-lg p-4 space-y-3" data-testid="seo-gsc-card">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <p className="font-semibold text-sm">🔍 Google Search Console</p>
            <a href={`https://search.google.com/search-console/welcome?resource_id=${encodeURIComponent(siteBase)}`} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 underline" data-testid="seo-gsc-create-link">Search Console'da doğrula ↗</a>
          </div>
          <Field label="HTML etiketi doğrulama kodu" hint="Google'ın verdiği tam meta etiketini yapıştırabilirsiniz (örn. &lt;meta name=&quot;google-site-verification&quot; content=&quot;...&quot;/&gt;) — content değeri otomatik ayıklanır.">
            <Input
              value={f.gsc_code || ""}
              onChange={(e) => {
                const raw = e.target.value || "";
                const m = raw.match(/content\s*=\s*["']([A-Za-z0-9_\-]+)["']/i);
                set("gsc_code")(m ? m[1] : raw.trim());
              }}
              placeholder="örn. abcDEF123XYZ_mn4... veya tam meta etiketi"
              data-testid="seo-gsc"
            />
          </Field>
          {!gscValid && <p className="text-xs text-red-600" data-testid="seo-gsc-invalid">⚠ Biçim hatalı — harf/rakam/alt-çizgi/tire, 20-100 karakter</p>}
          {f.gsc_code && gscValid && <p className="text-xs text-emerald-700" data-testid="seo-gsc-ok">✓ Site <code className="bg-stone-100 px-1 rounded text-[10px]">meta name="google-site-verification"</code> ile doğrulanacak</p>}
        </div>

        <div className="border rounded-lg p-4 space-y-3" data-testid="seo-robots-card">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <p className="font-semibold text-sm">🤖 robots.txt</p>
            <div className="flex items-center gap-2">
              <select value={robotsTone} onChange={(e) => setRobotsTone(e.target.value)} className="h-8 px-2 rounded-md border text-xs bg-white" data-testid="seo-robots-tone">
                <option value="standart">Dengeli (önerilen)</option>
                <option value="strict">Koruyucu (AI botları blokla)</option>
                <option value="permissive">Açık (en az kural)</option>
              </select>
              <Button type="button" size="sm" onClick={generateRobots} disabled={robotsBusy} data-testid="seo-robots-generate">
                {robotsBusy ? "Üretiliyor…" : "✨ AI ile oluştur"}
              </Button>
            </div>
          </div>
          <label className="flex items-center gap-3 text-sm"><Switch checked={f.robots_index !== false} onCheckedChange={set("robots_index")} data-testid="seo-robots-index" />Arama motorları siteyi dizine eklesin</label>
          <Field label="Ek kurallar (opsiyonel)" hint="AI ile oluşturun veya elle yazın. Güvenlik için &lt; ve &gt; karakterleri otomatik temizlenir.">
            <Textarea rows={5} value={f.robots_extra || ""} onChange={set("robots_extra")} className="font-mono text-xs" data-testid="seo-robots-extra" />
          </Field>
          <p className="text-xs text-stone-500">Sitemap: <a className="underline" href="/api/sitemap.xml" target="_blank" rel="noreferrer">/api/sitemap.xml</a> · Robots: <a className="underline" href="/api/robots.txt" target="_blank" rel="noreferrer">/api/robots.txt</a></p>
        </div>
      </Card>
      <Redirects />
    </>
  );
}

function Language() {
  const { f, set, save, busy } = useSection("language");
  const [n, setN] = useState({ code: "", name: "" });
  const active = f.active || ["tr"];
  return (
    <Card title="Dil" sub="Türkçe ana dildir. Bir dilde içerik eksikse Türkçesi gösterilir." onSave={save} busy={busy} testid="settings-language">
      <label className="flex items-center gap-3 text-sm"><Switch checked disabled />Türkçe (ana dil)</label>
      <label className="flex items-center gap-3 text-sm"><Switch checked={active.includes("en")} onCheckedChange={(v) => set("active")(v ? ["tr", "en"] : ["tr"])} data-testid="lang-en-switch" />İngilizce (/en)</label>
      <Field label="Ek diller" hint="Ek diller içerik formlarında sekme olarak görünür; sitede yayınlanması Aşama 3'te.">
        <div className="flex flex-wrap gap-2 mb-3">
          {(f.extra || []).map((l) => (
            <span key={l.code} className="inline-flex items-center gap-2 border rounded-full pl-3 pr-1 py-1 text-sm">{l.name} ({l.code})
              <button onClick={() => set("extra")(f.extra.filter((x) => x.code !== l.code))} className="p-1"><Trash2 size={12} className="text-red-500" /></button></span>
          ))}
        </div>
        <div className="flex gap-2">
          <Input placeholder="Kod (de)" maxLength={2} value={n.code} onChange={(e) => setN({ ...n, code: e.target.value.toLowerCase().replace(/[^a-z]/g, "") })} className="w-28" data-testid="lang-new-code" />
          <Input placeholder="Ad (Almanca)" value={n.name} onChange={(e) => setN({ ...n, name: e.target.value })} data-testid="lang-new-name" />
          <Button type="button" variant="outline" onClick={() => { if (n.code.length === 2 && !["tr", "en"].includes(n.code)) { set("extra")([...(f.extra || []), n]); setN({ code: "", name: "" }); } }} data-testid="lang-add"><Plus size={15} /></Button>
        </div>
      </Field>
    </Card>
  );
}

function PressSources() {
  const { f, set, save, busy } = useSection("press");
  const DEFAULTS = [
    { name: "Haber Kıbrıs", domain: "haberkibris.com" },
    { name: "Kıbrıs Postası", domain: "kibrispostasi.com" },
    { name: "Gündem Kıbrıs", domain: "gundemkibris.com" },
    { name: "Kıbrıs 7/24", domain: "kibris724.com" },
    { name: "Kıbrıs Son Dakika", domain: "kibrissondakika.com" },
    { name: "Kıbrıs Time", domain: "kibristime.com" },
    { name: "KKTC Haber", domain: "haberkktc.com" },
    { name: "Ada Basını", domain: "adabasini.com" },
    { name: "Giynik Gazetesi", domain: "giynikgazetesi.com" },
    { name: "Özgür Gazete", domain: "ozgurgazetekibris.com" },
    { name: "Kıbrıs Yenigün", domain: "kibrisyenigun.com" },
    { name: "Ajans Cyprus", domain: "ajanscyprus.com" },
    { name: "Haberal Kıbrıslı", domain: "haberalkibrisli.com" },
    { name: "Detay Kıbrıs", domain: "detaykibris.com" },
    { name: "Kıbrıs Haberci", domain: "kibrishaberci.com" },
    { name: "Kıbrıs Manşet", domain: "kibrismanset.com" },
    { name: "Kıbrıs Gazetesi", domain: "kibrisgazetesi.com" },
    { name: "Yeni Düzen", domain: "yeniduzen.com" },
    { name: "Halkın Sesi", domain: "halkinsesikibris.com" },
    { name: "Diyalog Gazetesi", domain: "diyaloggazetesi.com" },
    { name: "Star Kıbrıs", domain: "starkibris.net" },
    { name: "Havadis Kıbrıs", domain: "havadiskibris.com" },
    { name: "Güneş Kıbrıs", domain: "guneskibris.com" },
    { name: "Vatan Gazetesi", domain: "vatangazetesi.com" },
    { name: "Yeni Bakış", domain: "yenibakisgazetesi.com" },
    { name: "Volkan Gazetesi", domain: "kibrisvolkan.net" },
    { name: "YeniÇağ Kıbrıs", domain: "yenicaggazetesi.com" },
    { name: "Kıbrıs Türk", domain: "kibristurk.com" },
    { name: "Bugün Kıbrıs", domain: "bugunkibris.com" },
    { name: "Kıbrıs Gerçek", domain: "kibrisgercek.com" },
    { name: "Bağımsız Gazete", domain: "bagimsiz.com" },
    { name: "BRT Haber", domain: "brtk.net" },
    { name: "Ada TV", domain: "adatv.tv" },
    { name: "Kıbrıs Genç TV", domain: "kibrisgenctv.com" },
    { name: "Kanal T", domain: "kanalt.com" },
    { name: "Kıbrıs TV", domain: "kibristv.com" },
    { name: "Kanal Sim", domain: "kanalsim.com" },
    { name: "GAÜ TV", domain: "gau.edu.tr" },
    { name: "DAÜ TV", domain: "emu.edu.tr" },
    { name: "LAÜ TV", domain: "eul.edu.tr" },
    { name: "YDÜ TV", domain: "neu.edu.tr" },
    { name: "TAK (Türk Ajansı Kıbrıs)", domain: "tak.gov.ct.tr" },
    { name: "Mağusa Haber Ajansı", domain: "mhahaber.com" },
    { name: "Ekonomi Kıbrıs", domain: "ekonomikibris.com" },
    { name: "Ortam Gazetesi", domain: "ortamgazetesi.com" },
    { name: "Barış Gazetesi", domain: "barisgazetesi.com" },
    { name: "Afrika Gazetesi", domain: "afrikagazetesi.com" },
    { name: "Kıbrıslı Gazetesi", domain: "kibrisligazetesi.net" },
  ];
  const sources = f.sources?.length ? f.sources : DEFAULTS;
  const [n, setN] = useState({ name: "", domain: "" });
  const update = (i, k, v) => set("sources")(sources.map((s, idx) => (idx === i ? { ...s, [k]: v } : s)));
  const remove = (i) => set("sources")(sources.filter((_, idx) => idx !== i));
  const add = () => {
    const name = n.name.trim();
    const domain = n.domain.trim().toLowerCase().replace(/^https?:\/\//, "").replace(/\/.*$/, "");
    if (!name || !/^[a-z0-9][a-z0-9.-]{2,}\.[a-z]{2,}$/.test(domain)) {
      toast.error("Ad ve geçerli alan adı girin (örn: kibrispostasi.com)");
      return;
    }
    if (sources.some((s) => s.domain === domain)) { toast.error("Bu alan adı zaten listede"); return; }
    set("sources")([...sources, { name, domain }]);
    setN({ name: "", domain: "" });
  };
  const resetDefaults = () => set("sources")(DEFAULTS);
  const categories = Array.isArray(f.categories) ? f.categories : [];
  const [newCat, setNewCat] = useState("");
  const addCat = () => {
    const v = newCat.trim();
    if (!v) return;
    if (categories.some((c) => c.toLowerCase() === v.toLowerCase())) { toast.error("Bu kategori zaten listede"); return; }
    set("categories")([...categories, v]);
    setNewCat("");
  };
  const rmCat = (c) => set("categories")(categories.filter((x) => x !== c));
  return (
    <Card title="Basın Arşivi — Arama Kaynakları" sub="Sayfadaki arama kutusu, aşağıdaki KKTC kaynaklarında girilen kelimeyi arar." onSave={save} busy={busy} testid="settings-press">
      <label className="flex items-center gap-3 text-sm text-stone-700"><Switch checked={f.search_enabled !== false} onCheckedChange={set("search_enabled")} data-testid="press-search-enabled" />Arama kutusunu basın arşivi sayfasında göster</label>
      <div className="border rounded-lg divide-y" data-testid="press-sources-list">
        {sources.map((s, i) => (
          <div key={i} className="grid grid-cols-[1fr_1fr_auto] gap-2 items-center p-2" data-testid={`press-source-${i}`}>
            <Input value={s.name} onChange={(e) => update(i, "name", e.target.value)} placeholder="Kaynak adı" data-testid={`press-source-name-${i}`} />
            <Input value={s.domain} onChange={(e) => update(i, "domain", e.target.value)} placeholder="alan.com" data-testid={`press-source-domain-${i}`} />
            <Button type="button" size="icon" variant="ghost" onClick={() => remove(i)} data-testid={`press-source-del-${i}`}><Trash2 size={14} className="text-red-500" /></Button>
          </div>
        ))}
        {!sources.length && <p className="p-4 text-sm text-stone-400">Kaynak yok.</p>}
      </div>
      <div className="grid grid-cols-[1fr_1fr_auto] gap-2 items-end">
        <Field label="Yeni kaynak adı"><Input value={n.name} onChange={(e) => setN({ ...n, name: e.target.value })} placeholder="Kıbrıs Postası" data-testid="press-new-name" /></Field>
        <Field label="Alan adı"><Input value={n.domain} onChange={(e) => setN({ ...n, domain: e.target.value })} placeholder="kibrispostasi.com" data-testid="press-new-domain" /></Field>
        <Button type="button" onClick={add} data-testid="press-add-source"><Plus size={15} /></Button>
      </div>
      <button type="button" onClick={resetDefaults} className="text-xs text-stone-500 hover:text-stone-800 underline" data-testid="press-reset-defaults">KKTC varsayılan listesine dön</button>

      <div className="pt-6 border-t mt-2 space-y-3">
        <div>
          <p className="text-sm font-semibold">Haber Kategorileri (gruplar)</p>
          <p className="text-xs text-stone-500">Basın arşivindeki haberleri bu kategorilere göre grupabilirsiniz. Haberlerde ayrıca serbest etiket (tag) de kullanılabilir.</p>
        </div>
        <div className="flex flex-wrap gap-1.5" data-testid="press-categories-list">
          {categories.length === 0 && <span className="text-xs text-stone-400">Henüz kategori yok.</span>}
          {categories.map((c) => (
            <span key={c} className="inline-flex items-center gap-1.5 bg-amber-50 border border-amber-200 rounded-full px-3 py-1 text-xs text-amber-900">
              {c}
              <button type="button" onClick={() => rmCat(c)} className="text-amber-700 hover:text-red-600" data-testid={`press-category-rm-${c}`}><Trash2 size={11} /></button>
            </span>
          ))}
        </div>
        <div className="grid grid-cols-[1fr_auto] gap-2 items-end max-w-md">
          <Field label="Yeni kategori"><Input value={newCat} onChange={(e) => setNewCat(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCat(); } }} placeholder="Röportaj, Haber, Köşe Yazısı..." data-testid="press-new-category" /></Field>
          <Button type="button" onClick={addCat} data-testid="press-add-category"><Plus size={15} /></Button>
        </div>
      </div>
    </Card>
  );
}

const TABS = [["general", "Genel", General], ["ai", "Yapay Zeka", AI], ["contact", "İletişim (site)", SiteContactTab], ["integrations", "Entegrasyonlar", Integrations], ["backup", "Yedekleme", Backup], ["press", "Basın Arşivi", PressSources], ["seo", "SEO", SEO], ["language", "Dil", Language], ["security", "Güvenlik", SecurityTab], ["system", "Sistem Sağlığı", SystemHealth], ["history", "Ayar geçmişi", HistoryTab]];

export default function Settings() {
  const initial = (() => {
    try {
      const q = new URLSearchParams(window.location.search);
      const t = q.get("tab");
      if (t && TABS.some(([k]) => k === t)) return t;
    } catch { /* ignore */ }
    return "general";
  })();
  return (
    <>
      <PageTitle title="Ayarlar" />
      <Tabs defaultValue={initial}>
        <TabsList className="mb-6 flex-wrap h-auto">
          {TABS.map(([k, l]) => <TabsTrigger key={k} value={k} data-testid={`admin-tab-${k}`}>{l}</TabsTrigger>)}
        </TabsList>
        {TABS.map(([k, , C]) => <TabsContent key={k} value={k}><C /></TabsContent>)}
      </Tabs>
    </>
  );
}
